import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import { CardContainer, MenuButton, ModalOpenButton } from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  FileCopy as FileCopyIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { Box, IconButton, List, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { differenceWith, groupBy } from "lodash-es";
import React from "react";
import { Link } from "react-router-dom";
import {
  ProfileListItem,
  ProfileListItemButton,
} from "../../../commons/ProfileListItem.js";
import {
  ViewMode,
  ViewModeMenuItem,
} from "../../../commons/ViewModeMenuItem.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import {
  AddContactRelationshipModal,
  ContactType,
} from "../../contact-links/AddContactRelationshipModal.js";
import { ContactIcon } from "../../contact-links/ContactIcon.js";
import { useRelationshipRoles } from "../../contact-links/useRelationshipRoles.js";
import { CrmCompanyEditModal } from "../../crm-companies/CrmCompanyEditModal.js";
import { CrmPersonEditModal } from "../../crm-persons/CrmPersonEditModal.js";
import { CrmSendEmailModal } from "../../crm/CrmSendEmailModal.js";
import { OrganisationAvatar } from "../../organisations/OrganisationAvatar.js";
import { UserAvatar } from "../../users/UserAvatar.js";
import {
  ProjectContactLinksFragment,
  ProjectLinkCrmOrganisationFragment,
  ProjectLinkCrmUserFragment,
  QuoteItemContactLinksFragment,
  QuoteItemLinkCrmOrganisationFragment,
  QuoteItemLinkCrmUserFragment,
  useProjectAddCrmOrganisationLinkMutation,
  useProjectAddCrmUserLinkMutation,
  useProjectRemoveCrmOrganisationLinkMutation,
  useProjectRemoveCrmUserLinkMutation,
} from "./ProjectOverviewContactsBox.generated.js";

interface Props {
  projectId: string;
  contacts: ProjectContactLinksFragment[];
  buildingContacts: QuoteItemContactLinksFragment[];
  canEdit?: boolean;
  refetchQueries?: string[];
}

export const ProjectOverviewContactsBox = ({
  contacts,
  buildingContacts,
  projectId,
  canEdit = false,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["CrmUsers", "Global"]);

  const [viewMode, setViewMode] = React.useState<ViewMode>(null);

  const client = useApolloClient();
  const [addUserLink] = useProjectAddCrmUserLinkMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [addOrganisationLink] = useProjectAddCrmOrganisationLinkMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [removeUserLink] = useProjectRemoveCrmUserLinkMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [removeOrganisationLink] = useProjectRemoveCrmOrganisationLinkMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const removeContact = React.useCallback(
    async (contact: ProjectContactLinksFragment) => {
      if (contact.__typename === "ProjectLinkCrmUser") {
        await removeUserLink({
          variables: {
            input: {
              crmUserId: contact.crmUser.id,
              projectId,
              linkAs: contact.linkAs,
            },
          },
        });
      } else {
        await removeOrganisationLink({
          variables: {
            input: {
              crmOrganisationId: contact.crmOrganisation.id,
              projectId,
              linkAs: contact.linkAs,
            },
          },
        });
      }
    },
    [removeOrganisationLink, removeUserLink, projectId]
  );

  const addNewContact = React.useCallback(
    async (contactType: ContactType, contactId: string, role: string) => {
      if (contactType === "user") {
        await addUserLink({
          variables: {
            input: {
              projectId,
              crmUserId: contactId,
              linkAs: role,
              data: {},
            },
          },
        });
      } else if (contactType === "organisation") {
        await addOrganisationLink({
          variables: {
            input: {
              projectId,
              crmOrganisationId: contactId,
              linkAs: role,
              data: {},
            },
          },
        });
      } else {
        throw new Error("Invalid contact id");
      }
    },
    [addOrganisationLink, addUserLink, projectId]
  );

  const addNewContactFromLink = React.useCallback(
    async (contact: QuoteItemContactLinksFragment) => {
      if (contact.__typename === "QuoteItemLinkCrmUser") {
        await addUserLink({
          variables: {
            input: {
              projectId,
              crmUserId: contact.crmUser.id,
              linkAs: contact.linkAs,
              data: {},
            },
          },
        });
      } else if (contact.__typename === "QuoteItemLinkCrmOrganisation") {
        await addOrganisationLink({
          variables: {
            input: {
              projectId,
              crmOrganisationId: contact.crmOrganisation.id,
              linkAs: contact.linkAs,
              data: {},
            },
          },
        });
      } else {
        throw new Error("Invalid contact information");
      }
    },
    [addOrganisationLink, addUserLink, projectId]
  );

  const context = React.useMemo(
    () =>
      ({
        type: "PROJECT",
        id: projectId,
      }) as const,
    [projectId]
  );

  const buildingContactsExcludingProjectContacts = differenceWith(
    buildingContacts,
    contacts,
    (bc, c) => {
      return (
        (c.__typename === "ProjectLinkCrmUser" &&
          bc.__typename === "QuoteItemLinkCrmUser" &&
          c.crmUser.id === bc.crmUser.id &&
          c.linkAs === bc.linkAs) ||
        (c.__typename === "ProjectLinkCrmOrganisation" &&
          bc.__typename === "QuoteItemLinkCrmOrganisation" &&
          c.crmOrganisation.id === bc.crmOrganisation.id &&
          c.linkAs === bc.linkAs)
      );
    }
  );

  const groupedContacts = groupBy(contacts, contact =>
    contact.__typename === "ProjectLinkCrmUser"
      ? contact.crmUser.id
      : contact.__typename === "ProjectLinkCrmOrganisation"
        ? contact.crmOrganisation.id
        : undefined
  );
  const groupedBuildingContacts = groupBy(
    buildingContactsExcludingProjectContacts,
    contact =>
      contact.__typename === "QuoteItemLinkCrmUser"
        ? contact.crmUser.id
        : contact.__typename === "QuoteItemLinkCrmOrganisation"
          ? contact.crmOrganisation.id
          : undefined
  );

  return (
    <CardContainer
      Icon={<ContactIcon />}
      title={t("Contacts", { ns: "CrmUsers" })}
      itemCount={
        contacts.length + buildingContactsExcludingProjectContacts.length
      }
      ActionButton={
        canEdit ? (
          <Stack direction="row" spacing={1}>
            <MenuButton>
              <ViewModeMenuItem
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                allowedModes={[null, "delete"]}
              />
            </MenuButton>
            <ModalOpenButton
              Modal={AddContactRelationshipModal}
              modalProps={{
                title: t("Add contact to project", { ns: "CrmUsers" }),
                existingContactLinks: contacts,
                handleComplete: addNewContact,
              }}
            >
              <IconButton size="small" color="primary">
                <AddIcon />
              </IconButton>
            </ModalOpenButton>
          </Stack>
        ) : undefined
      }
    >
      <List disablePadding>
        {Object.values(groupedContacts).map(contacts => {
          const contact = contacts[0];
          return contact.__typename === "ProjectLinkCrmUser" ? (
            <ProjectLinkCrmUser
              key={contact.id}
              canEdit={canEdit}
              projectId={projectId}
              removeContact={removeContact}
              viewMode={viewMode}
              contacts={contacts as ProjectLinkCrmUserFragment[]}
            />
          ) : contact.__typename === "ProjectLinkCrmOrganisation" ? (
            <ProjectLinkCrmOrganisation
              key={contact.id}
              canEdit={canEdit}
              projectId={projectId}
              removeContact={removeContact}
              viewMode={viewMode}
              contacts={contacts as ProjectLinkCrmOrganisationFragment[]}
            />
          ) : (
            assertNever(contact)
          );
        })}
        {Object.values(groupedBuildingContacts).map(contacts => {
          const contact = contacts[0];
          return contact.__typename === "QuoteItemLinkCrmUser" ? (
            <QuoteItemLinkCrmUser
              key={contact.id}
              canEdit={canEdit}
              contacts={contacts as QuoteItemLinkCrmUserFragment[]}
              addNewContactFromLink={addNewContactFromLink}
            />
          ) : contact.__typename === "QuoteItemLinkCrmOrganisation" ? (
            <QuoteItemLinkCrmOrganisation
              key={contact.id}
              canEdit={canEdit}
              contacts={contacts as QuoteItemLinkCrmOrganisationFragment[]}
              addNewContactFromLink={addNewContactFromLink}
            />
          ) : (
            assertNever(contact)
          );
        })}
      </List>
    </CardContainer>
  );
};

const QuoteItemLinkCrmUser = ({
  canEdit,
  contacts,
  addNewContactFromLink,
}: {
  canEdit: boolean;
  contacts: QuoteItemLinkCrmUserFragment[];
  addNewContactFromLink: (
    contact: QuoteItemLinkCrmUserFragment
  ) => Promise<void>;
}) => {
  const { t } = useTranslate(["CrmUsers", "Global"]);
  const { getRoleLabel } = useRelationshipRoles();

  const contact = contacts[0];
  const primaryLabel = contact.crmUser.fullname;
  const avatar = <UserAvatar userAvatar={contact.crmUser} />;

  const phone = contact.crmUser.phones[0]?.number;

  const actionButtons = [];
  if (phone) {
    actionButtons.push(
      <IconButton
        key="phone"
        color="primary"
        component="a"
        href={`tel:${phone}`}
      >
        <PhoneIcon />
      </IconButton>
    );
  }
  if (canEdit) {
    actionButtons.push(
      <ModalOpenButton
        key="copy"
        Modal={ConfirmModal}
        modalProps={{
          handleConfirm: async () => {
            contacts.forEach(contact => {
              addNewContactFromLink(contact);
            });
          },
        }}
      >
        <Tooltip title={t("Copy contact to project", { ns: "CrmUsers" })}>
          <Box display="flex">
            <IconButton color="primary">
              <FileCopyIcon />
            </IconButton>
          </Box>
        </Tooltip>
      </ModalOpenButton>
    );
  }

  const roles = contacts
    .sort((a, b) => a.linkAs.localeCompare(b.linkAs))
    .map(contact => getRoleLabel(contact.linkAs) ?? contact.linkAs)
    .join(", ");
  const to = `/crm/organisations/${contact.crmUser.crmCompany.id}/users/${contact.crmUser.id}`;

  return (
    <ModalOpenButton
      Modal={CrmPersonEditModal}
      modalProps={{
        crmCompanyId: contact.crmUser.crmCompany.id,
        crmPersonId: contact.crmUser.id,
      }}
    >
      <ProfileListItemButton
        avatar={avatar}
        primary={primaryLabel}
        secondary={roles}
        ActionButtons={actionButtons}
        listItemProps={{
          onClick: e => e.preventDefault(), // in order to open modal
          component: Link,
          to: to,
        }}
      />
    </ModalOpenButton>
  );
};

const QuoteItemLinkCrmOrganisation = ({
  canEdit,
  contacts,
  addNewContactFromLink,
}: {
  canEdit: boolean;
  contacts: QuoteItemLinkCrmOrganisationFragment[];
  addNewContactFromLink: (
    contact: QuoteItemLinkCrmOrganisationFragment
  ) => Promise<void>;
}) => {
  const { t } = useTranslate(["CrmUsers", "Global"]);
  const { getRoleLabel } = useRelationshipRoles();

  const contact = contacts[0];
  const primaryLabel = contact.crmOrganisation.title;
  const avatar = (
    <OrganisationAvatar organisationAvatar={contact.crmOrganisation} size="s" />
  );

  const phone = contact.crmOrganisation.phones[0]?.number;

  const actionButtons = [];
  if (phone) {
    actionButtons.push(
      <IconButton
        key="phone"
        color="primary"
        component="a"
        href={`tel:${phone}`}
      >
        <PhoneIcon />
      </IconButton>
    );
  }
  if (canEdit) {
    actionButtons.push(
      <ModalOpenButton
        key="copy"
        Modal={ConfirmModal}
        modalProps={{
          handleConfirm: async () => {
            contacts.forEach(contact => {
              addNewContactFromLink(contact);
            });
          },
        }}
      >
        <Tooltip title={t("Copy contact to project", { ns: "CrmUsers" })}>
          <Box display="flex">
            <IconButton color="primary">
              <FileCopyIcon />
            </IconButton>
          </Box>
        </Tooltip>
      </ModalOpenButton>
    );
  }

  const roles = contacts
    .sort((a, b) => a.linkAs.localeCompare(b.linkAs))
    .map(contact => getRoleLabel(contact.linkAs) ?? contact.linkAs)
    .join(", ");
  const to = `/crm/organisations/${contact.crmOrganisation.id}`;

  return (
    <ModalOpenButton
      Modal={CrmCompanyEditModal}
      modalProps={{ crmCompanyId: contact.crmOrganisation.id }}
    >
      <ProfileListItemButton
        size="s"
        avatar={avatar}
        primary={primaryLabel}
        secondary={roles}
        ActionButtons={actionButtons}
        listItemProps={{
          onClick: e => e.preventDefault(), // in order to open modal
          component: Link,
          to: to,
        }}
      />
    </ModalOpenButton>
  );
};

const ProjectLinkCrmUser = ({
  canEdit,
  projectId,
  contacts,
  removeContact,
  viewMode,
}: {
  canEdit: boolean;
  projectId: string;
  contacts: ProjectLinkCrmUserFragment[];
  removeContact: (contact: ProjectLinkCrmUserFragment) => Promise<void>;
  viewMode: ViewMode;
}) => {
  const { getRoleLabel } = useRelationshipRoles();

  const contact = contacts[0];
  const context = React.useMemo(
    () =>
      ({
        type: "PROJECT",
        id: projectId,
      }) as const,
    [projectId]
  );

  const primaryLabel = contact.crmUser.fullname;
  const avatar = <UserAvatar userAvatar={contact.crmUser} />;
  const phone = contact.crmUser.phones[0]?.number;
  const hasEmail = Boolean(contact.crmUser.email);
  const recipient = hasEmail
    ? {
        id: contact.crmUser.id,
        label: `${contact.crmUser.fullname} <${contact.crmUser.email}>`,
        value: contact.crmUser.id,
        email: contact.crmUser.email,
        crmCompanyId: contact.crmUser.crmCompany.id,
        crmPersonId: contact.crmUser.id,
      }
    : null;

  const actionButtons = [];
  if (phone) {
    actionButtons.push(
      <IconButton
        key="phone"
        color="primary"
        component="a"
        href={`tel:${phone}`}
      >
        <PhoneIcon />
      </IconButton>
    );
  }
  if (recipient) {
    actionButtons.push(
      <ModalOpenButton
        Modal={CrmSendEmailModal}
        modalProps={{
          emailTemplateContext: "PROJECT",
          context,
          recipientOptions: [recipient],
          recipient,
          projectId,
        }}
      >
        <IconButton color="primary">
          <EmailIcon />
        </IconButton>
      </ModalOpenButton>
    );
  }
  if (canEdit && viewMode === "delete") {
    actionButtons.push(
      <ModalOpenButton
        key="delete"
        Modal={ConfirmModal}
        modalProps={{
          handleConfirm: async () => {
            contacts.forEach(contact => {
              removeContact(contact);
            });
          },
        }}
      >
        <IconButton color="primary">
          <DeleteIcon />
        </IconButton>
      </ModalOpenButton>
    );
  }

  const roles = contacts
    .sort((a, b) => a.linkAs.localeCompare(b.linkAs))
    .map(contact => getRoleLabel(contact.linkAs) ?? contact.linkAs)
    .join(", ");
  const to = `/crm/organisations/${contact.crmUser.crmCompany.id}/users/${contact.crmUser.id}`;

  return (
    <ModalOpenButton
      Modal={CrmPersonEditModal}
      modalProps={{
        crmCompanyId: contact.crmUser.crmCompany.id,
        crmPersonId: contact.crmUser.id,
      }}
    >
      <ProfileListItemButton
        avatar={avatar}
        primary={primaryLabel}
        secondary={roles}
        ActionButtons={actionButtons}
        listItemProps={{
          onClick: e => e.preventDefault(), // in order to open modal
          component: Link,
          to: to,
        }}
      />
    </ModalOpenButton>
  );
};

const ProjectLinkCrmOrganisation = ({
  canEdit,
  projectId,
  contacts,
  removeContact,
  viewMode,
}: {
  canEdit: boolean;
  projectId: string;
  contacts: ProjectLinkCrmOrganisationFragment[];
  removeContact: (contact: ProjectLinkCrmOrganisationFragment) => Promise<void>;
  viewMode: ViewMode;
}) => {
  const { getRoleLabel } = useRelationshipRoles();

  const contact = contacts[0];
  const context = React.useMemo(
    () =>
      ({
        type: "PROJECT",
        id: projectId,
      }) as const,
    [projectId]
  );

  const primaryLabel = contact.crmOrganisation.title;
  const avatar = (
    <OrganisationAvatar organisationAvatar={contact.crmOrganisation} size="s" />
  );
  const phone = contact.crmOrganisation.phones[0]?.number;
  const hasEmail = Boolean(contact.crmOrganisation.email);
  const recipient = hasEmail
    ? {
        id: contact.crmOrganisation.id,
        label: `${contact.crmOrganisation.title} <${contact.crmOrganisation.email}>`,
        value: contact.crmOrganisation.id,
        email: contact.crmOrganisation.email,
        crmCompanyId: contact.crmOrganisation.id,
        crmPersonId: null,
      }
    : null;

  const actionButtons = [];
  if (phone) {
    actionButtons.push(
      <IconButton
        key="phone"
        color="primary"
        component="a"
        href={`tel:${phone}`}
      >
        <PhoneIcon />
      </IconButton>
    );
  }
  if (recipient) {
    actionButtons.push(
      <ModalOpenButton
        Modal={CrmSendEmailModal}
        modalProps={{
          emailTemplateContext: "PROJECT",
          context,
          recipientOptions: [recipient],
          recipient,
          projectId,
        }}
      >
        <IconButton color="primary">
          <EmailIcon />
        </IconButton>
      </ModalOpenButton>
    );
  }
  if (canEdit && viewMode === "delete") {
    actionButtons.push(
      <ModalOpenButton
        key="delete"
        Modal={ConfirmModal}
        modalProps={{
          handleConfirm: async () => {
            contacts.forEach(contact => {
              removeContact(contact);
            });
          },
        }}
      >
        <IconButton color="primary">
          <DeleteIcon />
        </IconButton>
      </ModalOpenButton>
    );
  }

  const roles = contacts
    .sort((a, b) => a.linkAs.localeCompare(b.linkAs))
    .map(contact => getRoleLabel(contact.linkAs) ?? contact.linkAs)
    .join(", ");
  const to = `/crm/organisations/${contact.crmOrganisation.id}`;

  return (
    <ModalOpenButton
      Modal={CrmCompanyEditModal}
      modalProps={{ crmCompanyId: contact.crmOrganisation.id }}
    >
      <ProfileListItemButton
        size="s"
        avatar={avatar}
        primary={primaryLabel}
        secondary={roles}
        ActionButtons={actionButtons}
        listItemProps={{
          onClick: e => e.preventDefault(), // in order to open modal
          component: Link,
          to: to,
        }}
      />
    </ModalOpenButton>
  );
};
